import React, { useState, useRef, useEffect } from 'react';
import { Send, X, Bot, User, Loader2, Volume2, VolumeX, Settings, Copy, Check } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { useBusinessId } from '../hooks/use-business-id';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  audioUrl?: string; // Add audio URL to messages
  functionCalls?: Array<{
    function: string;
    arguments: any;
    result: any;
  }>; // Track function calls made by AI
  undoData?: {
    isUndoable: boolean;
    originalBookingState?: any;
    canUndo: boolean;
    undoInProgress?: boolean;
    modificationType?: string;
    bookingIdentifier?: string;
  }; // Track undo information for booking changes
}

interface NexusOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  stopSpeechRef?: React.MutableRefObject<(() => void) | null>;
  onThinkingStateChange?: (isThinking: boolean) => void;
  onSpeechStateChange?: (isSpeaking: boolean) => void;
  onAudioLevelChange?: (level: number) => void;
  onBookingUpdate?: () => void; // New callback for booking updates
}

const NexusOverlay: React.FC<NexusOverlayProps> = ({ isOpen, onClose, stopSpeechRef, onThinkingStateChange, onSpeechStateChange, onAudioLevelChange, onBookingUpdate }) => {
  const { user } = useAuth();
  const businessId = useBusinessId();
  const { selectedBusiness } = useBusiness();
  
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false); // Add streaming state
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null); // Track streaming message
  const [isSpeechEnabled, setIsSpeechEnabled] = useState(false); // DISABLED by default for faster responses
  const [isSpeechPlaying, setIsSpeechPlaying] = useState(false);
  const [hasUserGesture, setHasUserGesture] = useState(false);
  const [audioContextState, setAudioContextState] = useState<'suspended' | 'running' | 'closed'>('suspended');
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingAbortController = useRef<AbortController | null>(null); // For aborting streams

  // Initialize AudioContext on first user interaction
  const initializeAudioContext = async () => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        setAudioContextState(audioContextRef.current.state);
        console.log('🔊 AudioContext initialized:', audioContextRef.current.state);
      } catch (error) {
        console.error('Failed to initialize AudioContext:', error);
      }
    }

    // Resume if suspended
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
        setAudioContextState(audioContextRef.current.state);
        console.log('🔊 AudioContext resumed:', audioContextRef.current.state);
      } catch (error) {
        console.error('Failed to resume AudioContext:', error);
      }
    }
  };

  // Auto-scroll to bottom when new messages are added
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Audio analysis function
  const analyzeAudio = () => {
    if (analyserRef.current) {
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Calculate average volume
      const sum = dataArray.reduce((a, b) => a + b, 0);
      const average = sum / dataArray.length;
      const normalizedLevel = average / 255; // Normalize to 0-1
      
      onAudioLevelChange?.(normalizedLevel);
      
      if (currentAudioRef.current && !currentAudioRef.current.paused) {
        animationFrameRef.current = requestAnimationFrame(analyzeAudio);
      } else {
        onAudioLevelChange?.(0);
      }
    }
  };

  // Stop any playing audio
  const stopCurrentSpeech = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setIsSpeechPlaying(false);
    onSpeechStateChange?.(false);
    onAudioLevelChange?.(0);
    
    // Don't close AudioContext here, keep it for reuse
  };

  // Expose stop function to parent component
  useEffect(() => {
    if (stopSpeechRef) {
      stopSpeechRef.current = stopCurrentSpeech;
    }
    return () => {
      if (stopSpeechRef) {
        stopSpeechRef.current = null;
      }
    };
  }, [stopSpeechRef]);

  // API base URL - use local development server for function calling testing
  const AI_SERVICE_URL = 'https://booking-app-production-d357.up.railway.app';

  // Auto-focus input when overlay opens and initialize audio
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      
      // Don't automatically assume user gesture from opening chat
      // PWAs require explicit user interaction on UI elements
      console.log('🎵 Chat opened - waiting for user interaction to establish audio permission');
    }
  }, [isOpen]);

  // Handle ESC key to close overlay
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [isOpen, onClose]);

  // Simplified audio playback optimized for PWA autoplay
  const playAudio = async (audioUrl: string, messageId?: string) => {
    if (!isSpeechEnabled) {
      console.log('🔇 Speech disabled, skipping audio playback');
      return;
    }

    try {
      // For autoplay, require established permission
      if (messageId && !hasUserGesture) {
        console.log('🚫 Auto-play blocked - no audio permission established');
        return;
      }
      
      // Stop any currently playing speech first
      stopCurrentSpeech();
      
      console.log('🎵 Playing audio:', {
        audioEnabled: isSpeechEnabled,
        hasGesture: hasUserGesture,
        messageId,
        isAutoplay: !!messageId
      });
      
      const audio = new Audio();
      audio.crossOrigin = 'anonymous';
      audio.preload = 'auto';
      audio.src = audioUrl;
      
      currentAudioRef.current = audio;
      
      // Simplified event handlers
      audio.onplay = () => {
        console.log('▶️ Audio playback started');
        setIsSpeechPlaying(true);
        onSpeechStateChange?.(true);
        
        // Only set up analysis if AudioContext is available and running
        if (audioContextRef.current?.state === 'running') {
          try {
            analyserRef.current = audioContextRef.current.createAnalyser();
            analyserRef.current.fftSize = 256;
            
            const source = audioContextRef.current.createMediaElementSource(audio);
            source.connect(analyserRef.current);
            analyserRef.current.connect(audioContextRef.current.destination);
            
            analyzeAudio();
            console.log('🎛️ Audio analysis started');
          } catch (error) {
            console.log('⚠️ Audio analysis setup failed, continuing without analysis');
          }
        }
      };
      
      audio.onended = () => {
        console.log('⏹️ Audio playback ended');
        setIsSpeechPlaying(false);
        onSpeechStateChange?.(false);
        onAudioLevelChange?.(0);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        analyserRef.current = null;
        currentAudioRef.current = null;
      };
      
      audio.onerror = (e) => {
        console.error('🚨 Audio playback error:', e);
        setIsSpeechPlaying(false);
        onSpeechStateChange?.(false);
        onAudioLevelChange?.(0);
        currentAudioRef.current = null;
      };
      
      // Attempt to play
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('🎉 Audio play successful');
          })
          .catch(error => {
            console.error('🚨 Audio play failed:', error);
            if (error.name === 'NotAllowedError') {
              console.log('🚫 Audio blocked by browser policy');
            }
            setIsSpeechPlaying(false);
            onSpeechStateChange?.(false);
            onAudioLevelChange?.(0);
            currentAudioRef.current = null;
          });
      }
      
    } catch (error) {
      console.error('🚨 Audio setup error:', error);
      setIsSpeechPlaying(false);
      onSpeechStateChange?.(false);
      onAudioLevelChange?.(0);
    }
  };

  // Manual audio play function for fallback buttons
  const playMessageAudio = async (audioUrl: string, messageId: string) => {
    // This is triggered by user click, so gesture is guaranteed
    setHasUserGesture(true);
    await playAudio(audioUrl, messageId);
  };

  // Real audio permission test - consume user gesture for actual audio playback
  const establishAudioPermission = async () => {
    if (hasUserGesture) return true;

    try {
      console.log('🎵 Testing real audio permission...');
      
      // Create and test the actual audio pipeline that we'll use for AI responses
      const testAudio = new Audio();
      testAudio.crossOrigin = 'anonymous';
      testAudio.preload = 'auto';
      
      // Use a very short silent audio file (base64 encoded)
      testAudio.src = 'data:audio/wav;base64,UklGRjIAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQ4AAAAAAAAAAAAA';
      
      // Test actual audio playback - this consumes the user gesture
      await testAudio.play();
      console.log('✅ Real audio test successful - user gesture consumed');
      
      // Clean up test audio
      testAudio.pause();
      testAudio.src = '';
      
      // Now initialize AudioContext AFTER successful audio test
      await initializeAudioContext();
      
      setHasUserGesture(true);
      console.log('🎉 Audio permission fully established');
      return true;
      
    } catch (error) {
      console.log('⚠️ Real audio test failed:', error);
      return false;
    }
  };

  // Enhanced speech toggle that tests real audio permission
  const handleSpeechToggle = async () => {
    const newSpeechState = !isSpeechEnabled;
    setIsSpeechEnabled(newSpeechState);
    
    // If enabling speech, test audio permission immediately
    if (newSpeechState && !hasUserGesture) {
      console.log('🎵 Speech enabled - testing audio permission...');
      await establishAudioPermission();
    }
  };

  // Handle text input focus to test audio permission
  const handleInputFocus = async () => {
    if (!hasUserGesture) {
      console.log('🎵 Input focused - testing audio permission...');
      await establishAudioPermission();
    }
  };

  // Handle send button click to test audio permission
  const handleSendClick = async () => {
    // Don't block sending for audio permission issues
    if (!hasUserGesture) {
      console.log('🎵 Send clicked - testing audio permission...');
      establishAudioPermission().catch(() => {
        console.log('🔄 Audio permission failed - will retry on speech toggle');
      });
    }
    
    // Always call sendMessage regardless of audio permission status
    await sendMessage();
  };

  // Copy message to clipboard
  const copyMessage = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy message:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedMessageId(messageId);
        setTimeout(() => {
          setCopiedMessageId(null);
        }, 2000);
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
      }
      document.body.removeChild(textArea);
    }
  };

  // Smart detection for function calls vs streaming
  const requiresFunctionCalls = (message: string): boolean => {
    const messageLower = message.toLowerCase();
    
    // Function call keywords - booking operations
    const functionCallKeywords = [
      // Booking modifications
      'reschedule', 'move booking', 'change booking', 'modify booking',
      'reschedule booking', 'move appointment', 'change appointment',
      'shift booking', 'extend booking', 'shorten booking', 'extend appointment',
      
      // Status changes
      'mark as completed', 'mark completed', 'set status', 'change status',
      'mark as in progress', 'mark in progress', 'complete booking',
      'mark as scheduled', 'cancel booking', 'cancel appointment',
      'mark', 'as completed', 'as in progress', 'as scheduled',
      
      // Time/date changes
      'change time', 'change date', 'new time', 'new date',
      'different time', 'different date', 'move to',
      
      // Duration changes
      'add day', 'add time', 'extend by', 'shorten by', 'reduce by',
      'make longer', 'make shorter', 'add another day', 'remove day',
      
      // Cancellations
      'cancel', 'cancelled', 'delete booking', 'remove booking'
    ];
    
    // Booking identifier patterns (booking numbers, customer names with booking context)
    const bookingPatterns = [
      /booking\s+\d+/,           // "booking 1456"
      /booking\s+#\d+/,          // "booking #1456"  
      /#\d+/,                    // "#1456"
      /\b\d{3,5}\b/,             // standalone numbers like "1456"
    ];
    
    // Check for function call keywords
    const hasKeywords = functionCallKeywords.some(keyword => messageLower.includes(keyword));
    
    // Check for booking identifier patterns
    const hasBookingPattern = bookingPatterns.some(pattern => pattern.test(messageLower));
    
    // Must have both booking context AND action keywords, OR specific booking modification phrases
    const hasBookingAction = hasKeywords && (hasBookingPattern || messageLower.includes('booking') || messageLower.includes('appointment'));
    
    // Special cases - these always require function calls even without explicit booking IDs
    const specialCases = [
      'mark today complete',
      'complete today',
      'mark all completed',
      'update status',
      'change status to'
    ];
    
    const hasSpecialCase = specialCases.some(special => messageLower.includes(special));
    
    return hasBookingAction || hasSpecialCase;
  };

  // Add streaming send message function
  const sendMessageStreaming = async () => {
    const textToSend = inputMessage.trim();
    if (!textToSend || isLoading || isStreaming) {
      console.log('⚠️ Message send blocked:', { textToSend: !!textToSend, isLoading, isStreaming });
      return;
    }

    console.log('📤 Sending streaming message:', textToSend);

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: textToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setIsStreaming(true);
    onThinkingStateChange?.(true);

    // Create assistant message for streaming
    const assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      functionCalls: []
    };

    setMessages(prev => [...prev, assistantMessage]);
    setStreamingMessageId(assistantMessageId);

    try {
      // Prepare conversation history for context
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Create abort controller for this stream
      const abortController = new AbortController();
      streamingAbortController.current = abortController;

      // Use the new streaming endpoint
      const response = await fetch(`${AI_SERVICE_URL}/chat-streaming`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: textToSend,
          business_id: selectedBusiness?.id,
          speech_enabled: isSpeechEnabled,
          voice: 'nova',
          speed: 1.1,
          conversation_history: conversationHistory
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error('Failed to get AI streaming response');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response stream available');
      }

      let streamedContent = '';
      let functionCalls: any[] = [];

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              switch (data.type) {
                case 'status':
                  // Update status (like "Thinking...")
                  setMessages(prev => 
                    prev.map(msg => 
                      msg.id === assistantMessageId 
                        ? { ...msg, content: data.content }
                        : msg
                    )
                  );
                  break;
                  
                case 'token':
                  // Stream tokens in real-time
                  if (data.content) {
                    streamedContent += data.content;
                    setMessages(prev => 
                      prev.map(msg => 
                        msg.id === assistantMessageId 
                          ? { ...msg, content: streamedContent }
                          : msg
                      )
                    );
                  }
                  break;
                  
                case 'function_call':
                  // Show function call status
                  console.log('🔧 Function call:', data.function_name, data.function_args);
                  functionCalls.push({
                    function: data.function_name,
                    arguments: data.function_args,
                    result: null
                  });
                  
                  setMessages(prev => 
                    prev.map(msg => 
                      msg.id === assistantMessageId 
                        ? { ...msg, functionCalls }
                        : msg
                    )
                  );
                  break;
                  
                case 'function_result':
                  // Update function result
                  if (functionCalls.length > 0) {
                    functionCalls[functionCalls.length - 1].result = data.content;
                  }
                  break;
                  
                case 'audio':
                  // Handle audio when streaming completes
                  if (data.audio_url && isSpeechEnabled && hasUserGesture) {
                    console.log('🎵 Playing streaming audio');
                    setTimeout(() => {
                      playAudio(data.audio_url, assistantMessageId);
                    }, 100);
                  }
                  break;
                  
                case 'complete':
                  // Streaming complete
                  console.log('✅ Stream completed');
                  setMessages(prev => 
                    prev.map(msg => 
                      msg.id === assistantMessageId 
                        ? { ...msg, content: data.content, functionCalls }
                        : msg
                    )
                  );
                  
                  // 🔄 TRIGGER UI UPDATES FOR BOOKING MODIFICATIONS (STREAMING)
                  emitBookingUpdateEvent(functionCalls);
                  break;
                  
                case 'error':
                  // Handle streaming errors
                  console.error('❌ Stream error:', data.content);
                  setMessages(prev => 
                    prev.map(msg => 
                      msg.id === assistantMessageId 
                        ? { ...msg, content: 'Sorry, I encountered an error. Please try again.' }
                        : msg
                    )
                  );
                  break;
              }
            } catch (e) {
              console.error('Error parsing streaming data:', e);
            }
          }
        }
      }

    } catch (error) {
      console.error('Error streaming message:', error);
      
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Stream aborted by user');
      } else {
        setMessages(prev => 
          prev.map(msg => 
            msg.id === assistantMessageId 
              ? { ...msg, content: 'Sorry, I\'m having trouble connecting. Please try again.' }
              : msg
          )
        );
      }
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingMessageId(null);
      onThinkingStateChange?.(false);
      streamingAbortController.current = null;
    }
  };

  // Helper function to emit booking update events
  const emitBookingUpdateEvent = (functionCalls: any[]) => {
    if (!functionCalls || functionCalls.length === 0) return;

    // Check if any function calls modified bookings
    const bookingModifications = functionCalls.filter(fc => 
      fc.function && [
        'modify_booking',
        'update_booking_status', 
        'reschedule_booking',
        'cancel_booking',
        'update_booking_schedule'
      ].includes(fc.function)
    );

    if (bookingModifications.length > 0) {
      console.log('🔄 Nexus modified bookings, triggering UI updates:', bookingModifications.map(fc => fc.function));
      
      // Emit custom event for immediate calendar refresh (similar to CreateBookingModal)
      window.dispatchEvent(new CustomEvent('nexusBookingUpdate', {
        detail: { 
          businessId: selectedBusiness?.id,
          modifications: bookingModifications.map(fc => ({
            function: fc.function,
            bookingId: fc.result?.booking_id || fc.arguments?.booking_id,
            success: fc.result?.success !== false
          }))
        }
      }));
      
      // Call the callback prop if provided
      if (onBookingUpdate) {
        onBookingUpdate();
      }
      
      console.log('📅 Dispatched nexusBookingUpdate event for immediate UI refresh');
    }
  };

  // Regular function call handler for booking operations
  const sendMessageWithFunctions = async () => {
    const textToSend = inputMessage.trim();
    if (!textToSend || isLoading || isStreaming) {
      console.log('⚠️ Function call blocked:', { textToSend: !!textToSend, isLoading, isStreaming });
      return;
    }

    console.log('🔧 Sending function call message:', textToSend);

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: textToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    onThinkingStateChange?.(true);

    try {
      // Prepare conversation history for context
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Use the regular function calling endpoint
      const response = await fetch(`${AI_SERVICE_URL}/chat-with-functions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: textToSend,
          business_id: selectedBusiness?.id,
          speech_enabled: isSpeechEnabled,
          voice: 'nova',
          speed: 1.1,
          conversation_history: conversationHistory
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get AI function response');
      }

      const data = await response.json();
      
      // Extract undo data from function results if available
      let undoData: Message['undoData'] = undefined;
      if (data.function_calls && data.function_calls.length > 0) {
        // Check if any function call has undo data
        const undoableCall = data.function_calls.find((fc: any) => 
          fc.result && fc.result.undo_data && fc.result.undo_data.is_undoable
        );
        
        if (undoableCall) {
          undoData = {
            isUndoable: true,
            originalBookingState: undoableCall.result.original_booking_state,
            canUndo: true,
            undoInProgress: false,
            modificationType: undoableCall.result.undo_data.modification_type,
            bookingIdentifier: undoableCall.result.undo_data.booking_identifier
          };
        }
      }
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        audioUrl: data.audio_url,
        functionCalls: data.function_calls || [],
        undoData
      };

      setMessages(prev => [...prev, assistantMessage]);

      // 🔄 TRIGGER UI UPDATES FOR BOOKING MODIFICATIONS
      emitBookingUpdateEvent(data.function_calls || []);

      // Play audio if available and speech is enabled
      if (data.audio_url && isSpeechEnabled && hasUserGesture) {
        setTimeout(() => {
          playAudio(data.audio_url, assistantMessage.id);
        }, 100);
      }

    } catch (error) {
      console.error('Error with function call message:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I\'m having trouble processing that request. Please try again.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      onThinkingStateChange?.(false);
    }
  };

  // Hybrid sendMessage - smart routing between streaming and function calls
  const sendMessage = async () => {
    const textToSend = inputMessage.trim();
    
    if (!textToSend) {
      return;
    }

    // Smart detection: use function calls for booking operations, streaming for everything else
    const needsFunctionCalls = requiresFunctionCalls(textToSend);
    
    console.log(`🤖 Message analysis: "${textToSend}" ${needsFunctionCalls ? '→ Function calls' : '→ Streaming'}`);
    
    if (needsFunctionCalls) {
      // Route to function calling endpoint for booking operations
      await sendMessageWithFunctions();
    } else {
      // Route to streaming endpoint for regular chat
      await sendMessageStreaming();
    }
  };

  // Add cancel streaming function
  const cancelStreaming = () => {
    if (streamingAbortController.current) {
      streamingAbortController.current.abort();
      console.log('🛑 Streaming cancelled by user');
    }
  };

  // Undo booking change function
  const undoBookingChange = async (messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (!message || !message.undoData || !message.undoData.canUndo) {
      console.error('Cannot undo: Invalid message or undo data');
      return;
    }

    // Update message to show undo in progress
    setMessages(prev => prev.map(msg => 
      msg.id === messageId && msg.undoData
        ? { ...msg, undoData: { ...msg.undoData, undoInProgress: true } }
        : msg
    ));

    try {
      console.log('🔄 Undoing booking change...');
      
      const response = await fetch(`${AI_SERVICE_URL}/undo-booking-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_id: selectedBusiness?.id,
          original_booking_state: message.undoData.originalBookingState,
          modification_type: message.undoData.modificationType
        })
      });

      if (!response.ok) {
        throw new Error('Failed to undo booking change');
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Booking change undone successfully');
        
        // Update message to remove undo capability and add undo confirmation
        setMessages(prev => prev.map(msg => 
          msg.id === messageId
            ? { 
                ...msg, 
                content: msg.content + '\n\n✅ **Change undone** - Booking restored to original state',
                undoData: { ...msg.undoData!, canUndo: false, undoInProgress: false }
              }
            : msg
        ));
      } else {
        throw new Error(result.error || 'Undo failed');
      }

    } catch (error) {
      console.error('❌ Undo failed:', error);
      
      // Reset undo progress state
      setMessages(prev => prev.map(msg => 
        msg.id === messageId && msg.undoData
          ? { ...msg, undoData: { ...msg.undoData, undoInProgress: false } }
          : msg
      ));

      // Show error message
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'Sorry, I couldn\'t undo that change. Please try manually or contact support.',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isStreaming) {
        cancelStreaming();
      } else {
        handleSendClick();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Messages Display Area - Top Right */}
      <div className="absolute top-20 right-4 bottom-40 md:bottom-48 left-4 md:left-auto md:max-w-md overflow-y-auto space-y-3 pointer-events-auto pr-2 pl-2 md:pl-0 pb-4 scrollbar-thin scrollbar-thumb-purple-400/50 scrollbar-track-transparent" style={{ WebkitOverflowScrolling: 'touch' }}>
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`
                max-w-xs md:max-w-sm px-3 md:px-4 py-2 md:py-3 rounded-xl md:rounded-2xl backdrop-blur-xl border shadow-lg
                animate-in slide-in-from-top-2 fade-in duration-300
                ${message.role === 'user'
                  ? 'bg-gradient-to-r from-purple-500/90 to-purple-600/90 border-purple-300/30 text-white shadow-purple-500/20'
                  : 'bg-white/90 border-white/30 text-gray-800 shadow-white/20'
                }
              `}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-2 flex-1">
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-5 h-5 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                        <Bot className="w-3 h-3 text-white" />
                      </div>
                    </div>
                  )}
                  <div className="flex-1">
                    <div className={`text-sm md:text-base leading-relaxed ${message.role === 'user' ? 'text-white' : 'text-gray-800'}`}>
                      {isStreaming && message.id === streamingMessageId ? (
                        <div className="flex items-center space-x-2">
                          <span>{message.content}</span>
                          <Loader2 className="w-3 h-3 animate-spin text-purple-500" />
                        </div>
                      ) : (
                        message.content
                      )}
                    </div>
                    
                    {/* Function Call Indicators */}
                    {message.functionCalls && message.functionCalls.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {message.functionCalls.map((fc, index) => (
                          <div key={index} className="text-xs bg-gradient-to-r from-orange-500/20 to-orange-600/20 border border-orange-400/30 text-orange-700 px-2 py-1 rounded-lg backdrop-blur-sm">
                            🔧 {fc.function}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Undo Button for booking modifications */}
                    {message.undoData?.isUndoable && message.undoData?.canUndo && (
                      <div className="mt-2 flex items-center space-x-2">
                        <button
                          onClick={() => undoBookingChange(message.id)}
                          disabled={message.undoData?.undoInProgress}
                          className="text-xs bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-400/30 text-red-700 px-2 py-1 rounded-lg hover:from-red-500/30 hover:to-red-600/30 transition-all duration-200 disabled:opacity-50 backdrop-blur-sm"
                        >
                          {message.undoData?.undoInProgress ? (
                            <span className="flex items-center space-x-1">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              <span>Undoing...</span>
                            </span>
                          ) : (
                            `Undo ${message.undoData?.modificationType || 'Change'}`
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Message Actions */}
                <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                  {message.role === 'assistant' && message.audioUrl && isSpeechEnabled && (
                    <button
                      onClick={() => playMessageAudio(message.audioUrl!, message.id)}
                      className="p-1 hover:bg-white/20 rounded-lg transition-colors backdrop-blur-sm"
                      title="Play audio"
                    >
                      <Volume2 className="w-3 h-3 text-gray-600 hover:text-purple-600" />
                    </button>
                  )}
                  
                  {/* Copy button for assistant messages */}
                  {message.role === 'assistant' && (
                    <button
                      onClick={() => copyMessage(message.id, message.content)}
                      className="p-1 hover:bg-white/20 rounded-lg transition-colors backdrop-blur-sm"
                      title="Copy message"
                    >
                      {copiedMessageId === message.id ? (
                        <Check className="w-3 h-3 text-green-500" />
                      ) : (
                        <Copy className="w-3 h-3 text-gray-600 hover:text-purple-600" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Text Input Area - Inline with Nexus on mobile, bottom center on desktop */}
      <div className="absolute bottom-20 md:bottom-20 left-4 md:left-1/2 md:transform md:-translate-x-1/2 w-88 md:w-full md:max-w-lg pr-20 md:pr-0 md:px-6 pointer-events-auto">
        <div className="relative">
          {/* Input Container */}
          <div className="backdrop-blur-xl bg-white/90 border border-white/30 rounded-2xl shadow-lg p-4 animate-in slide-in-from-bottom-2 fade-in duration-300">
            <div className="flex items-end space-x-3">
              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-100/50 rounded-full transition-colors backdrop-blur-sm"
                title="Close Nexus"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Text Input */}
              <div className="flex-1">
                <div className="relative">
                  <textarea
                    ref={inputRef}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    onFocus={handleInputFocus}
                    placeholder="Ask Nexus"
                    className="w-full px-0 py-2 bg-transparent border-none resize-none focus:outline-none text-gray-800 placeholder-gray-500"
                    rows={1}
                    style={{ maxHeight: '120px' }}
                    disabled={isLoading}
                  />
                  
                  {/* Real-time mode indicator */}
                  {inputMessage.trim() && !isLoading && !isStreaming && (
                    <div className="absolute -top-6 left-0">
                      {requiresFunctionCalls(inputMessage) ? (
                        <div className="px-2 py-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs rounded-full font-medium shadow-lg">
                          🔧 Function Call
                        </div>
                      ) : (
                        <div className="px-2 py-1 bg-gradient-to-r from-green-500 to-green-600 text-white text-xs rounded-full font-medium shadow-lg">
                          Streaming
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Speech Toggle with Mode indicators */}
              <div className="relative">
                <button
                  onClick={handleSpeechToggle}
                  className={`p-2 rounded-full transition-colors backdrop-blur-sm ${
                    isSpeechEnabled 
                      ? 'text-gray-700 hover:bg-white/40' 
                      : 'text-gray-400 hover:bg-white/40'
                  }`}
                  title={isSpeechEnabled ? 'Disable speech' : 'Enable speech'}
                >
                  {isSpeechEnabled ? (
                    <Volume2 className="w-4 h-4" />
                  ) : (
                    <VolumeX className="w-4 h-4" />
                  )}
                </button>
                {isSpeechPlaying && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-gradient-to-r from-green-500 to-green-600 rounded-full animate-pulse" />
                )}
              </div>

              {/* Send Button */}
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="p-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-full hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-purple-500/25 backdrop-blur-sm"
                title="Send message"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>

              {/* Cancel streaming button */}
              {isStreaming && (
                <button
                  onClick={cancelStreaming}
                  className="p-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg hover:shadow-red-500/25 backdrop-blur-sm"
                  title="Cancel streaming"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NexusOverlay; 